import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

// Re-export shared utilities
export * from '@repo/utils/string'

// Types
export interface ScrollOptions {
  behavior?: ScrollBehavior
  block?: ScrollLogicalPosition
  updateHistory?: boolean
}

export interface ScrollToElementOptions extends ScrollOptions {
  offset?: number
  delayMs?: number
}

export interface ScrollTrackingOptions {
  threshold?: number
  rootMargin?: string
  passive?: boolean
}

// Default configurations
const DEFAULT_SCROLL_OPTIONS: Required<ScrollOptions> = {
  behavior: 'smooth',
  block: 'start',
  updateHistory: true,
}

// Utility functions
const stripHash = (hash: string): string => hash.replace(/^#/, '').trim()

const findElement = (selector: string): HTMLElement | null => {
  if (typeof document === 'undefined') return null

  return (
    document.getElementById(selector) ??
    (document.querySelector(`[data-anchor="${selector}"]`) as HTMLElement | null)
  )
}

// Core scroll functions
export const scrollToElement = (
  selector: string,
  options: ScrollToElementOptions = {}
): boolean => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  const element = findElement(selector)
  if (!element) return false

  const {
    behavior,
    updateHistory,
    offset = 0,
    delayMs = 0,
  } = {
    ...DEFAULT_SCROLL_OPTIONS,
    ...options,
  }

  const scrollTo = () => {
    const elementRect = element.getBoundingClientRect()
    const absoluteElementTop = elementRect.top + window.pageYOffset
    const offsetPosition = absoluteElementTop - offset

    window.scrollTo({
      top: offsetPosition,
      left: 0,
      behavior,
    })

    if (updateHistory && selector.startsWith('#')) {
      const newHash = `#${stripHash(selector)}`
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash)
      }
    }
  }

  if (delayMs > 0) {
    setTimeout(scrollTo, delayMs)
  } else {
    scrollTo()
  }

  return true
}

export const scrollToHash = (hash: string, options: ScrollOptions = {}): boolean => {
  return scrollToElement(hash, options)
}

// Watch for element to appear and scroll to it
export const watchAndScroll = (
  selector: string,
  options: ScrollToElementOptions = {}
): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {}
  }

  // Try immediate scroll first
  if (scrollToElement(selector, options)) {
    return () => {}
  }

  let timeoutId: number
  let animationFrame: number

  const attemptScroll = () => {
    if (scrollToElement(selector, options)) {
      cleanup()
    }
  }

  const scheduleAttempt = () => {
    timeoutId = window.setTimeout(() => {
      animationFrame = window.requestAnimationFrame(attemptScroll)
    }, options.delayMs || 50)
  }

  const observer = new MutationObserver(() => {
    if (scrollToElement(selector, options)) {
      cleanup()
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })
  scheduleAttempt()

  function cleanup() {
    if (timeoutId) window.clearTimeout(timeoutId)
    if (animationFrame) window.cancelAnimationFrame(animationFrame)
    observer.disconnect()
  }

  return cleanup
}

// Handle anchor navigation from click events
export const handleAnchorClick = (
  event: MouseEvent<HTMLElement>,
  href?: string,
  options: ScrollOptions = {}
): boolean => {
  if (
    event.defaultPrevented ||
    !href ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return false
  }

  try {
    const targetUrl = new URL(href, window.location.href)
    const currentUrl = new URL(window.location.href)

    // Only handle same-page anchor links
    if (targetUrl.pathname !== currentUrl.pathname || !targetUrl.hash) {
      return false
    }

    if (scrollToHash(targetUrl.hash, options)) {
      event.preventDefault()
      return true
    }
  } catch {
    return false
  }

  return false
}

// Hook for tracking scroll position and active elements
export const useScrollTracking = (
  selector: string = '[data-scroll-item]',
  options: ScrollTrackingOptions = {}
) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollTimeoutRef = useRef<number | undefined>(undefined)

  const { threshold = 0.25, passive = true } = options

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true)

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current)
      }

      // Set new timeout to detect scroll end
      scrollTimeoutRef.current = window.setTimeout(() => {
        setIsScrolling(false)
      }, 150)

      const windowHeight = window.innerHeight
      const elements = document.querySelectorAll(selector)
      let newActiveIndex = 0

      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect()
        const elementTop = rect.top
        const elementBottom = rect.bottom
        const thresholdPosition = windowHeight * threshold

        if (elementTop <= thresholdPosition && elementBottom > thresholdPosition) {
          newActiveIndex = index
        }
      })

      // Keep the last item active when scrolled past all sections
      const lastElement = elements[elements.length - 1]
      if (lastElement) {
        const lastRect = lastElement.getBoundingClientRect()
        if (lastRect.bottom <= windowHeight * threshold) {
          newActiveIndex = elements.length - 1
        }
      }

      if (newActiveIndex !== activeIndex) {
        setActiveIndex(newActiveIndex)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive })
    handleScroll() // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [activeIndex, selector, threshold, passive])

  return { activeIndex, isScrolling }
}

// Hook for scroll restoration (simplified version of TanStack Router's approach)
export const useScrollRestoration = (enabled: boolean = true) => {
  const previousPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const handlePopState = () => {
      // Let the browser handle scroll restoration on back/forward
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [enabled])

  return { previousPathRef }
}

// Export legacy functions for backward compatibility
export { scrollToHash as watchHashAndScroll }
export { handleAnchorClick as handleAnchorNavigation }
